import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import * as TogglePrimitive from "@radix-ui/react-toggle";
import { tv } from "tailwind-variants";
import { CircleXIcon } from "lucide-react";
import { backgroundSurfaceActiveVariants, backgroundSurfaceHoverVariants, backgroundSurfaceOnVariants, backgroundSurfaceVariants, borderOnVariants, outlinedCompoundVariants, semanticKeys, textOnVariants, textVariants, } from "../../theme/variants/sharedVariants";
import cn from "../../utils/cn";
export const ChipSemanticKeys = [...semanticKeys, "secondary", "accessory2", "accessory5"]; // TODO: remove secondary, accessory2, accessory5 when update dashboard
const chipVariants = tv({
    base: cn("flex items-center justify-center", "rounded-2xl border border-gray-4", "bg-white px-3.5 text-xs font-medium text-gray-10 transition-colors duration-200 ", "hover:bg-white active:scale-95 active:ring-0 active:transition-all disabled:active:scale-100"),
    variants: {
        variant: {
            default: "",
        },
        color: {
            default: "",
            primary: "",
            success: "",
            warning: "",
            attention: "",
            danger: "",
            purple: "",
            sky: "",
            "danger-dark": "",
            secondary: "",
            accessory2: "",
            accessory5: "",
        },
        size: {
            regular: "h-8 py-1",
            condensed: "h-6 py-0.5",
            md: "h-8 py-1",
            sm: "h-6 py-0.5",
        },
        disabled: {
            true: "cursor-not-allowed pointer-events-none bg-gray-4 border-gray opacity-50 ",
            false: "",
        },
    },
    compoundVariants: [
        ...outlinedCompoundVariants.map((item) => ({
            variant: "default",
            color: item.color,
            className: cn(`hover:${textVariants[item.color]}`, textOnVariants[item.color], borderOnVariants[item.color], backgroundSurfaceOnVariants[item.color], backgroundSurfaceHoverVariants[item.color], backgroundSurfaceActiveVariants[item.color]),
        })),
        // TODO: remove when update dashboard
        {
            variant: "default",
            color: "secondary",
            className: cn(`hover:${textVariants.default}`, `data-[state=on]:${textVariants.default}`, `data-[state=on]:${borderOnVariants.default}`, `data-[state=on]:${backgroundSurfaceVariants.default}`, borderOnVariants.default, backgroundSurfaceHoverVariants.default, backgroundSurfaceActiveVariants.default),
        },
        // TODO: remove when update dashboard
        {
            variant: "default",
            color: "accessory2",
            className: cn(`hover:${textVariants.purple}`, `data-[state=on]:${textVariants.purple}`, `data-[state=on]:${borderOnVariants.purple}`, `data-[state=on]:${backgroundSurfaceVariants.purple}`, borderOnVariants.purple, backgroundSurfaceHoverVariants.purple, backgroundSurfaceActiveVariants.purple),
        },
        // TODO: remove when update dashboard
        {
            variant: "default",
            color: "accessory5",
            className: cn(`hover:${textVariants.sky}`, `data-[state=on]:${textVariants.sky}`, `data-[state=on]:${borderOnVariants.sky}`, `data-[state=on]:${backgroundSurfaceVariants.sky}`, borderOnVariants.sky, backgroundSurfaceHoverVariants.sky, backgroundSurfaceActiveVariants.sky),
        },
    ],
    defaultVariants: {
        color: "default",
        variant: "default",
        size: "regular",
        disabled: false,
    },
});
const removeIconVariant = tv({
    base: "hover:text-gray-11",
    variants: {
        size: {
            regular: "size-5",
            condensed: "size-4",
            md: "size-5",
            sm: "size-4",
        },
    },
    defaultVariants: {
        size: "regular",
    },
});
const Chip = React.forwardRef(({ className, children, counter, color, variant, icon, size, deleteIcon, onDeleteClick, disabled, ...props }, ref) => {
    const customIcon = (newIcon) => {
        if (!newIcon)
            return null;
        return React.cloneElement(newIcon, {
            className: cn(newIcon?.props?.className || "size-4 mr-1"),
        });
    };
    const pressRemoveClick = onDeleteClick
        ? (e) => {
            onDeleteClick();
            e.stopPropagation();
        }
        : undefined;
    return (_jsxs(TogglePrimitive.Root, { ref: ref, disabled: disabled, className: cn(chipVariants({
            color: color,
            variant: variant,
            size: size,
            disabled: disabled,
        }), className), ...props, children: [customIcon(icon), counter !== undefined && _jsx("div", { className: "mr-1 font-semibold", children: counter }), children, deleteIcon && (_jsx(CircleXIcon, { onClick: pressRemoveClick, "data-testid": "remove-icon", className: cn("-mr-1 ml-2", removeIconVariant({
                    size: size,
                })) })), deleteIcon === false && _jsx("span", { "data-testid": "remove-icon-placeholder", className: "-mr-1 ml-1 size-4" })] }));
});
Chip.displayName = TogglePrimitive.Root.displayName;
export { chipVariants, removeIconVariant };
export default Chip;
